import posthog from 'posthog-js';
import type { QuestionCategory } from '@/types/database';

const TOKEN = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as
  | string
  | undefined;
const HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

export const analyticsEnabled = Boolean(TOKEN && HOST);

export function initAnalytics() {
  if (!analyticsEnabled) return;
  posthog.init(TOKEN!, {
    api_host: HOST!,
    defaults: '2026-01-30',
  });
  posthog.register({ platform: 'web' });
}

type OnboardingStep =
  | 'name'
  | 'interview_date'
  | 'english_level'
  | 'concerns'
  | 'value_preview'
  | 'quick_win'
  | 'soft_paywall';

type PracticeCategory = 'government' | 'history' | 'civics';

type ProfileField =
  | 'name'
  | 'interview_date'
  | 'english_comfort_level'
  | 'nervous_about';

export type WaitlistCtaSource =
  | 'nav_get_started'
  | 'hero_cta'
  | 'final_cta'
  | 'load_error_signup'
  | 'continue_after_result'
  | 'submit_error_signup';

type AnalyticsEvent =
  | { name: 'signup_completed'; props: { method: 'email' } }
  | { name: 'login_completed'; props: { method: 'email' } }
  | { name: 'onboarding_step_completed'; props: { step: OnboardingStep } }
  | {
      name: 'onboarding_completed';
      props: { converted_at_paywall: boolean };
    }
  | {
      name: 'practice_answer_submitted';
      props: {
        question_id: string;
        category: PracticeCategory;
        input_mode: 'voice' | 'text';
        is_correct: boolean;
      };
    }
  | {
      name: 'landing_demo_answer_graded';
      props: {
        question_id: number;
        input_mode: 'voice' | 'text';
        is_correct: boolean;
      };
    }
  | { name: 'mock_test_started'; props?: undefined }
  | {
      name: 'mock_test_abandoned';
      props: { questions_answered: number; total_questions: number };
    }
  | {
      name: 'pricing_viewed';
      props: { source: 'onboarding' | 'settings' | 'home' | string };
    }
  | {
      name: 'checkout_started';
      props: { plan: 'monthly' | 'annual'; has_onboarding_discount: boolean };
    }
  | { name: 'billing_portal_opened'; props?: undefined }
  | { name: 'profile_updated'; props: { field: ProfileField } }
  | {
      name: 'notification_preference_toggled';
      props: { preference_key: string; enabled: boolean };
    }
  | { name: 'waitlist_cta_clicked'; props: { source: WaitlistCtaSource } }
  | { name: 'waitlist_signup_submitted'; props: { source: WaitlistCtaSource } }
  | {
      name: 'waitlist_signup_succeeded';
      props: { source: WaitlistCtaSource; already_subscribed: boolean };
    }
  | {
      name: 'waitlist_signup_failed';
      props: { source: WaitlistCtaSource; error_code: string };
    };

type EventName = AnalyticsEvent['name'];
type EventProps<N extends EventName> = Extract<
  AnalyticsEvent,
  { name: N }
>['props'];

export function track<N extends EventName>(
  ...args: EventProps<N> extends undefined
    ? [name: N]
    : [name: N, props: EventProps<N>]
): void {
  if (!analyticsEnabled) return;
  const [name, props] = args as [N, EventProps<N> | undefined];
  posthog.capture(name, props as Record<string, unknown> | undefined);
}

export function identifyUser(
  userId: string,
  traits?: { email?: string }
): void {
  if (!analyticsEnabled) return;
  posthog.identify(userId, traits);
}

export function resetAnalytics(): void {
  if (!analyticsEnabled) return;
  posthog.reset();
}

export function setPremiumProperty(isPremium: boolean): void {
  if (!analyticsEnabled) return;
  posthog.register({ is_premium: isPremium });
}

const CATEGORY_MAP: Record<QuestionCategory, PracticeCategory> = {
  'American Government': 'government',
  'American History': 'history',
  'Integrated Civics': 'civics',
};

export function mapPracticeCategory(c: QuestionCategory): PracticeCategory {
  return CATEGORY_MAP[c];
}
